import { useQuery } from "@tanstack/react-query";

import type { GraphDataContent } from "@components/Graph/Graph.types";
import { useGroupReferences } from "@contexts/GroupReferences/GroupReferences.context";
import type { Energy } from "@services/services.types";

import { apiEcmsHttp } from "../http";
import { QUERY_KEYS } from "../keys";
import type { GetConsumptionGraph } from "./apiEcms.types";

const selectGraphData = ({ data }: { data: GetConsumptionGraph }) => {
  const sortedConsumptions = [...data.monthlyConsumption].sort(
    (a, b) => a.year - b.year || a.month - b.month,
  );
  const formattedConsumptions: GraphDataContent[] = sortedConsumptions.map(
    (consumption) => ({
      ...consumption,
      period: `${consumption.year}-${String(consumption.month).padStart(2, "0")}`,
      consumptionInKwh: Number(consumption.consumptionInKwh),
    }),
  );

  const dataValues = formattedConsumptions.map(
    ({ consumptionInKwh }) => consumptionInKwh,
  );

  const biggestValue = Math.max(...dataValues);

  return {
    ...data,
    data: formattedConsumptions,
    monthlyConsumption: sortedConsumptions,
    biggestValue,
  };
};

const getConsumptionGraph = (energy: Energy, signal: AbortSignal) =>
  apiEcmsHttp.get<GetConsumptionGraph>("/consumption-graph", {
    params: { energy },
    signal,
  });

export const useGetConsumptionGraph = (energy: Energy) => {
  const { currentGroup } = useGroupReferences();

  return useQuery({
    queryKey: [QUERY_KEYS.CONSUMPTION_GRAPH, energy, currentGroup],
    queryFn: ({ signal }) => getConsumptionGraph(energy, signal),
    staleTime: 86400000, // One day
    select: selectGraphData,
  });
};
